import { defineStore } from 'pinia';
import { computed } from 'vue';
import { configurePermissionContext, isEndAllowed, type DataScope } from '@/core';
import { useAuthStore } from './auth';
import { PERM } from '@/permissions';

const CURRENT_END = (import.meta.env.VITE_APP_END || 'BPO') as 'ADMIN' | 'BPO' | 'MINIAPP';

/**
 * permission store（录单端裁剪版）— 只保留「当前登录态能看什么」。
 *
 * 与管理端同名 store 的差异是**结构性**的：本仓库不含 `src/api/role.ts`，
 * 因此角色列表、权限树、权限版本与回滚这五类管理动作在这里既调不到接口、也渲染不出入口
 * （《前端文档》§5.2 权限边界 / PRD AC-M4-7）。BPO客服的 dataScope 恒为
 * `SELF_ENTERED_ONLY`，取值只来自服务端签发的快照，前端不做二次放大。
 */
export const usePermissionStore = defineStore('permission', () => {
  const auth = useAuthStore();

  const granted = computed(() => new Set(auth.permissions));
  const dataScope = computed<DataScope | undefined>(() => auth.user?.dataScope);

  /** 端绑定互斥：外部角色仅绑专属端；未声明绑定按「不放行」处理 */
  const endAllowed = computed(() => {
    const binding = auth.user?.endBinding ?? [];
    return binding.length > 0 ? isEndAllowed(CURRENT_END, binding) : false;
  });

  /** 录单端只认这一个业务写动作码，取值来自 `@/permissions`（服务端快照真源） */
  const canCreateOrder = computed(() => granted.value.has(PERM.orderCreate));

  return { granted, dataScope, endAllowed, canCreateOrder };
});

/** 把权限上下文注入 core，供 hasPerm / v-perm 使用（core 不依赖 store） */
export function wirePermissionContext(): void {
  configurePermissionContext(() => {
    const auth = useAuthStore();
    if (!auth.user) return null;
    return {
      permissions: auth.user.permissions,
      dataScope: auth.user.dataScope,
      endBinding: auth.user.endBinding,
      roleCode: auth.user.roleCode,
      builtInSuperAdmin: false,
    };
  });
}
